import {ChangeDetectionStrategy, Component, Input} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {BehaviorSubject} from "rxjs";
import {CALENDAR_TYPES, TDatePickerInput, TIntCalendarOutput, TIntRangeCalendarCollection} from "../../calendars";
import {getTimestamp} from "../../../utils";

@Component({
  selector: 'int-month-picker',
  templateUrl: './int-month-picker.component.html',
  styleUrls: ['../int-datepicker/int-datepicker.component.scss', './int-month-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: IntMonthPickerComponent,
      multi: true
    }
  ]
})
export class IntMonthPickerComponent implements ControlValueAccessor {

  @Input() type: keyof typeof CALENDAR_TYPES = 'single';
  @Input() placeholder: string | null = '';
  @Input() clearable = true;

  disabled$ = new BehaviorSubject(false);
  value$: BehaviorSubject<TDatePickerInput>
    = new BehaviorSubject<TDatePickerInput>(null);

  isFocus$: BehaviorSubject<boolean>
    = new BehaviorSubject<boolean>(false);

  show$ = new BehaviorSubject(false);

  get isActive() {
    return Boolean(this.displayDataFormatter) || this.isFocus$.value || this.show$.value;
  }

  get displayDataFormatter() {
    const payload = this.value$.value;
    const output = Array.isArray(payload)
      ? this.getDisplayedList(payload).filter(x => !!x).map(d => {
        return this.displayFormatter(d)
      }).join(' - ')
      : this.displayFormatter(payload);
    return (Boolean(payload) && !!output) ? output : null;
  }

  private displayFormatter(value: Date | null) {
    return value?.toLocaleDateString().split('.').filter((t, i) => i !== 0).join('.');
  }

  private isDatesEqual(payload: TIntRangeCalendarCollection): boolean {
    if(payload[0] && payload[1]) {
      return getTimestamp(payload[0]) === getTimestamp(payload[1])
    }
    return false;
  }

  private getDisplayedList(payload: TIntRangeCalendarCollection) {
    return this.isDatesEqual(payload) ? [payload[0]] : payload;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled$.next(isDisabled);
  }

  writeValue(payload: Date | [Date | null | string, Date | null | string] | null): void {

    const date = Array.isArray(payload)
      ? payload.map((value) => {
        return typeof value === "string" && Date.parse(value) ? new Date(value) : value;
      })
      : typeof payload === 'string' ? new Date(payload) : payload;

    //@ts-ignore
    this.setData(date);
  }

  setData(payload: Date | [Date | null, Date | null] | null) {
    const output = payload ? (Array.isArray(payload) ? payload : [payload, null]) : null;
    this.onChange(output);
    this.value$.next(payload);
  }

  clickOutSide() {
    this.show$.next(false);
  }

  onContainerClick() {
    this.show$.next(true);
  }

  onCalendarChange(payload: TIntCalendarOutput | null) {
    this.setData(payload);
  }

  onFocus() {
    if(this.disabled$.value) {
      return;
    }
    this.isFocus$.next(true);
  }

  onBlur() {
    this.isFocus$.next(false);
  }

  onClear(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.onCalendarChange(null);
    this.isFocus$.next(false);
    this.show$.next(false);
  }

  onChange(value: unknown) {}
  onTouched() {}
}
